// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

/*
  Reports are owned by a user. Beside the cross-site reports (type "site"), each report is
  bound to one site, so we only list the reports of the currently selected site here and
  offer to switch to the other sites the user holds reports for.
*/

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Form } from "react-bootstrap";

import { IAction, ActionType, raiseAction, IActionTypeEdit } from "actions";
import { Report } from "shared/types/cloudtypes";
import { isSet } from "shared/utils/utils";
import {
  isOcppFrontend,
  getBrowserTimezoneOffset,
  getCrossSiteReports,
  getSiteReports,
  tabClickHandler,
} from "guiutils";
import { IState } from "states/states";
import { IReportOptions } from "states/guistates";

import ModalReportSettings from "view/modal/ModalReportSettings";
import DateInput from "view/comp/DateInput";
import ReportEntry from "view/comp/ReportEntry";

interface IReportsExtProps {
  isDashboard?: boolean;
}

// a site the user holds reports for
interface IReportSite {
  id: number;
  name: string;
}

type IReportsProps = IReportOptions & {
  canEditReports: boolean;
  siteReports: Report[];
  crossSiteReports: Report[];
  otherSites: IReportSite[];
};

const mapStateToProps = (state: IState) => {
  const siteId = state.cached.selectedSite;
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const isOcppRestricted = isOcppFrontend(state.router.rootPath);

  // the other sites the user has reports for, in the order of the site list
  const reportSiteIds = (state.site.reportList ?? [])
    .filter((curItem) => isSet(curItem.siteId) && curItem.siteId !== siteId)
    .map((curItem) => curItem.siteId);
  const otherSites = (state.site.siteList ?? [])
    .filter((curSite) => reportSiteIds.includes(curSite.id))
    .map((curSite) => ({ id: curSite.id, name: curSite.name }));

  const props: IReportsProps = {
    ...state.gui.reportOptions,
    canEditReports: curUser?.accessRights.createReports ?? false,
    siteReports: getSiteReports(state.site.reportList, siteId, isOcppRestricted),
    crossSiteReports: getCrossSiteReports(state.site.reportList, isOcppRestricted),
    otherSites,
  };
  return props;
};

interface IReportsDispatch {
  onSetValue: (name: keyof IReportOptions, value: number | boolean | undefined) => void;
  onNewReport: () => void;
  onSelectSite: (siteId: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IReportsDispatch = {
    onSetValue: (name: keyof IReportOptions, value: number | boolean | undefined) => {
      const editPayload: IActionTypeEdit = {
        name: `reportOptions.${name}`,
        value,
      };
      dispatch(raiseAction(ActionType.ACT_EDIT_VALUE, editPayload));
    },
    onNewReport: () => {
      dispatch(raiseAction(ActionType.ACT_EDIT_REPORT, undefined));
    },
    onSelectSite: (siteId: number) => {
      dispatch(raiseAction(ActionType.ACT_SELECT_SITE, siteId));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  startFilterHint: {
    id: "start date",
  },
  endFilterHint: {
    id: "end date",
  },
});

class ReportsComponent extends React.Component<
  IReportsProps & IReportsExtProps & IReportsDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const isReadOnly = this.props.isDashboard === true || !this.props.canEditReports;

    return (
      <div id="reports">
        {this.props.isDashboard !== true && (
          <h1 className="mx-2">
            <FormattedMessage id="Reports" />
          </h1>
        )}
        <div>
          {this.renderTimeFilter()}
          {this.renderReportTable(
            "reportentries",
            <FormattedMessage id="Report Items" />,
            this.props.siteReports,
            isReadOnly,
            // new reports are always created for the current site
            !isReadOnly,
          )}
          {this.props.crossSiteReports.length > 0 &&
            this.renderReportTable(
              "crosssitereportentries",
              <FormattedMessage id="Cross-Site Reports" />,
              this.props.crossSiteReports,
              isReadOnly,
              false,
            )}
          {this.props.isDashboard !== true && this.renderOtherSites()}
        </div>
        <ModalReportSettings />
      </div>
    );
  }

  private renderReportTable(
    id: string,
    title: JSX.Element,
    reports: Report[],
    isReadOnly: boolean,
    showCreateButton: boolean,
  ) {
    const reportItems = reports
      // deactivated reports are only of interest for their owner
      .filter((curReport) => !isReadOnly || curReport.isActive)
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((curReport) => (
        <ReportEntry key={curReport.id} reportId={curReport.id} isReadOnly={isReadOnly} />
      ));

    return (
      <div id={id} className="reportsection">
        <div className="tableheader">{title}</div>
        <table>
          <tbody>
            {reportItems}
            {showCreateButton && (
              <tr>
                <td colSpan={2}>
                  <Button
                    id="newreport"
                    variant="secondary"
                    className="mt-1 mb-1"
                    onClick={(e) => this.props.onNewReport()}
                  >
                    <FormattedMessage id="Create New" />
                  </Button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // the sites, other than the current one, the user holds reports for
  private renderOtherSites() {
    if (this.props.otherSites.length === 0) {
      return <></>;
    }
    const siteItems = this.props.otherSites.map((curSite) => (
      <tr key={curSite.id}>
        <td>
          <span
            className="link"
            tabIndex={0}
            onKeyDown={tabClickHandler}
            onClick={() => this.props.onSelectSite(curSite.id)}
          >
            {curSite.name}
          </span>
        </td>
      </tr>
    ));

    return (
      <div id="reportsites" className="reportsection">
        <div className="tableheader">
          <FormattedMessage id="Reports of Other Sites" />
        </div>
        <table>
          <tbody>{siteItems}</tbody>
        </table>
      </div>
    );
  }

  private renderTimeFilter() {
    // today, 00:00
    const today =
      (Math.round(Date.now() / (24 * 60 * 60 * 1000)) * 24 * 60 * 60 + getBrowserTimezoneOffset()) *
      1000;
    return (
      <div id="reportoptions">
        <div className="tableheader mb-1">
          <FormattedMessage id="Report Options" />
        </div>
        <table>
          <tbody>
            <tr>
              <td>
                <Form.Check
                  id="limitStart"
                  checked={this.props.startDate ? true : false}
                  onChange={(event) => {
                    this.props.onSetValue(
                      "startDate",
                      event.currentTarget.checked ? today : undefined,
                    );
                  }}
                  label={
                    <>
                      <FormattedMessage id="Limit period from" />
                      {isSet(this.props.startDate) && this.props.startDate !== 0 && <>:</>}
                    </>
                  }
                />
              </td>
              <td>
                {isSet(this.props.startDate) && this.props.startDate !== 0 && (
                  <>
                    <DateInput
                      id="datetime_limitStart"
                      type="datetime-local"
                      style={{ minWidth: 210 }}
                      className="me-1"
                      title={this.context.formatMessage(messages.startFilterHint)}
                      valueMs={this.props.startDate}
                      onChange={(valueMs) => this.props.onSetValue("startDate", valueMs)}
                    />
                  </>
                )}
              </td>
            </tr>
            <tr>
              <td>
                <Form.Check
                  id="limitEnd"
                  checked={this.props.endDate ? true : false}
                  onChange={(event) => {
                    this.props.onSetValue(
                      "endDate",
                      event.currentTarget.checked ? today : undefined,
                    );
                  }}
                  label={
                    <>
                      <FormattedMessage id="Limit period until" />
                      {isSet(this.props.endDate) && this.props.endDate !== 0 && <>:</>}
                    </>
                  }
                />
              </td>
              <td>
                {isSet(this.props.endDate) && this.props.endDate !== 0 && (
                  <>
                    <DateInput
                      id="datetime_limitEnd"
                      type="datetime-local"
                      style={{ minWidth: 210 }}
                      title={this.context.formatMessage(messages.endFilterHint)}
                      valueMs={this.props.endDate}
                      onChange={(valueMs) => this.props.onSetValue("endDate", valueMs)}
                    />
                  </>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ReportsComponent);
